import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";

const ENDPOINTS = {
  scenathons: "/api/scenathon/admin/all",
  availablePathways: (scenathonId) =>
    `/api/scenathon/available-pathways/${encodeURIComponent(scenathonId)}`,
  defaultPathways: (scenathonId) =>
    `/api/scenathon/default-pathways/${encodeURIComponent(scenathonId)}`,
  tasks: (page, limit, search, colname) =>
    `/api/auth/admin/tasks?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ""}${colname ? `&colname=${encodeURIComponent(colname)}` : ""}`,
  taskColnames: "/api/auth/admin/tasks/colnames",
  tradeSubmissions: (scenathonId) =>
    `/api/tradeadjustment/submissions?scenathonId=${encodeURIComponent(scenathonId)}`,
  targetsSummary: (scenathonId) =>
    `/api/comparison/scenathon-indicators?scenathonId=${encodeURIComponent(scenathonId)}`,
};

const AUTO_REFRESH_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 30, label: "Every 30 seconds" },
  { value: 60, label: "Every 60 seconds" },
  { value: 300, label: "Every 5 minutes" },
];

const TAB_ITEMS = [
  { id: "tasks", label: "Task Monitoring" },
  { id: "trade", label: "Trade Adjustments" },
  { id: "targets", label: "Targets Summary" },
];

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
};

const STATUS_CLASSES = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-rose-100 text-rose-700",
};

const TYPE_LABELS = {
  Mini: "Mini",
  Normal: "Normal",
};

const TYPE_CLASSES = {
  Mini: "bg-sky-100 text-sky-700",
  Normal: "bg-indigo-100 text-indigo-700",
};

const TASK_STATUS_META = {
  pending: {
    label: "Pending",
    className: "bg-amber-100 text-amber-800",
  },
  in_progress: {
    label: "In progress",
    className: "bg-sky-100 text-sky-800",
  },
  completed: {
    label: "Completed",
    className: "bg-emerald-100 text-emerald-800",
  },
  error: {
    label: "Error",
    className: "bg-rose-100 text-rose-800",
  },
  unknown: {
    label: "Unknown",
    className: "bg-slate-100 text-slate-700",
  },
};

const INITIAL_DETAILS = {
  availablePathways: [],
  defaultPathways: [],
  loading: false,
  error: null,
};

const INITIAL_DATA_STATE = {
  rows: [],
  loading: false,
  error: null,
  lastRefreshed: null,
};

const INITIAL_TASK_FILTERS = {
  search: "",
  status: "all",
  country: "all",
  user: "all",
  name: "all",
  dateFrom: "",
  dateTo: "",
};

const INITIAL_TRADE_FILTERS = {
  search: "",
  pathway: "all",
  country: "all",
  user: "all",
  dateFrom: "",
  dateTo: "",
};

const CHART_COLORS = [
  "#0f766e",
  "#2563eb",
  "#7c3aed",
  "#c2410c",
  "#be123c",
  "#047857",
  "#0369a1",
  "#a16207",
];

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(value, maximumFractionDigits = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return number.toLocaleString("en-US", { maximumFractionDigits });
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) return date;

  // tasktodo.created_at is TEXT and may arrive without a timezone.
  const normalized = String(value).trim().replace(" ", "T");
  const normalizedDate = new Date(normalized);
  return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
}

function toTimestamp(value) {
  return parseDate(value)?.getTime() ?? null;
}

function inDateRange(value, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return true;
  const timestamp = toTimestamp(value);
  if (timestamp === null) return false;

  if (dateFrom) {
    const fromTimestamp = new Date(`${dateFrom}T00:00:00`).getTime();
    if (timestamp < fromTimestamp) return false;
  }

  if (dateTo) {
    const toTimestampValue = new Date(`${dateTo}T23:59:59.999`).getTime();
    if (timestamp > toTimestampValue) return false;
  }

  return true;
}

function normalizeText(value) {
  return String(value ?? "").trim().toLowerCase();
}

function uniqueSorted(values) {
  return [...new Set(values.filter((value) => value !== null && value !== undefined && String(value).trim() !== ""))]
    .sort((a, b) => String(a).localeCompare(String(b)));
}

function classifyTaskStatus(status) {
  const value = normalizeText(status).replace(/[\s-]+/g, "_");

  if (["pending", "queued", "new", "waiting", "created", "todo"].includes(value)) {
    return "pending";
  }

  if (["processing", "running", "in_progress", "started", "working"].includes(value)) {
    return "in_progress";
  }

  if (["completed", "complete", "done", "success", "successful", "finished"].includes(value)) {
    return "completed";
  }

  if (["error", "failed", "failure", "cancelled", "canceled"].includes(value)) {
    return "error";
  }

  return "unknown";
}

function asArray(payload, keys) {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
}

function displayUser(row) {
  return (
    row?.submitted_by ||
    row?.user_email ||
    row?.user_name ||
    row?.user_id ||
    "Unknown"
  );
}

function countBy(rows, valueGetter) {
  const counts = new Map();
  rows.forEach((row) => {
    const value = valueGetter(row) || "Unknown";
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || String(a.label).localeCompare(String(b.label)));
}

function getLatestDate(rows, valueGetter) {
  let latest = null;
  rows.forEach((row) => {
    const date = parseDate(valueGetter(row));
    if (date && (!latest || date > latest)) latest = date;
  });
  return latest;
}

async function extractErrorMessage(response, fallback) {
  try {
    const text = await response.text();
    if (!text) return fallback;

    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallback;
    } catch {
      if (text.startsWith("<!DOCTYPE")) return fallback;
      return text;
    }
  } catch {
    return fallback;
  }
}

async function fetchJson(authFetch, url, fallbackMessage) {
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, fallbackMessage));
  }
  return response.json();
}

function buildTargetSeriesLabel(row) {
  const explicit = row.series || row.series_name || row.label;
  if (explicit) return String(explicit);

  const parts = [
    row.country,
    row.pathway_name || row.pathway,
    row.region,
    row.scenario,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" • ") : "Value";
}

function normalizeTargetsPayload(payload) {
  const structuredTargets = asArray(payload, ["targets"]);

  if (
    structuredTargets.length > 0 &&
    structuredTargets.some((target) => Array.isArray(target?.series))
  ) {
    return structuredTargets.map((target, targetIndex) => ({
      id: target.id || target.target_id || `${target.name || target.target_name || "target"}-${targetIndex}`,
      name: target.name || target.target_name || target.target || `Target ${targetIndex + 1}`,
      unit: target.unit || target.target_unit || "",
      description: target.description || "",
      series: target.series.map((series, seriesIndex) => ({
        name: series.name || series.label || `Series ${seriesIndex + 1}`,
        points: asArray(series, ["points", "data"])
          .map((point, pointIndex) => ({
            x: point.x ?? point.year ?? point.period ?? point.category ?? pointIndex + 1,
            value: Number(point.value ?? point.target_value ?? point.y),
          }))
          .filter((point) => Number.isFinite(point.value)),
      })),
    }));
  }

  const rows = asArray(payload, ["rows", "data", "values"]);
  const targetMap = new Map();

  rows.forEach((row, rowIndex) => {
    try {
      const targetName =
        row.target_name ||
        row.target ||
        row.indicator_name ||
        row.indicator ||
        row.name;

      if (!targetName) return;

      const numericValue = Number(
        row.value ?? row.target_value ?? row.amount ?? row.y,
      );
      if (!Number.isFinite(numericValue)) return;

      const targetKey = String(targetName);
      if (!targetMap.has(targetKey)) {
        targetMap.set(targetKey, {
          id: row.target_id || targetKey,
          name: targetKey,
          unit: row.unit || row.target_unit || "",
          description: row.description || "",
          seriesMap: new Map(),
        });
      }

      const target = targetMap.get(targetKey);
      const seriesName = buildTargetSeriesLabel(row);
      if (!target.seriesMap.has(seriesName)) {
        target.seriesMap.set(seriesName, []);
      }

      target.seriesMap.get(seriesName).push({
        x: row.year ?? row.period ?? row.category ?? row.date ?? rowIndex + 1,
        value: numericValue,
      });
    } catch (error) {
      console.error("Unable to normalize target row", error, row);
    }
  });

  return [...targetMap.values()].map((target) => ({
    id: target.id,
    name: target.name,
    unit: target.unit,
    description: target.description,
    series: [...target.seriesMap.entries()].map(([name, points]) => ({
      name,
      points: [...points].sort((a, b) => {
        const aNumber = Number(a.x);
        const bNumber = Number(b.x);
        if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
          return aNumber - bNumber;
        }
        return String(a.x).localeCompare(String(b.x));
      }),
    })),
  }));
}

function MetricCard({ label, value, description, tone = "slate" }) {
  const toneClasses = {
    slate: "bg-slate-50 text-slate-900",
    emerald: "bg-emerald-50 text-emerald-800",
    sky: "bg-sky-50 text-sky-800",
    amber: "bg-amber-50 text-amber-800",
    rose: "bg-rose-50 text-rose-800",
    violet: "bg-violet-50 text-violet-800",
  };

  return (
    <div className={`rounded-3xl border border-slate-200 p-5 shadow-sm ${toneClasses[tone] || toneClasses.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="mt-3 text-3xl font-semibold">{value}</p>
      {description ? <p className="mt-2 text-sm opacity-70">{description}</p> : null}
    </div>
  );
}

function SectionHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{eyebrow}</p>
        <h3 className="mt-1 text-2xl font-semibold text-slate-900">{title}</h3>
        {description ? <p className="mt-2 max-w-3xl text-sm text-slate-600">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function RefreshButton({ loading, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? "Refreshing..." : "Refresh"}
    </button>
  );
}

function LastUpdated({ value }) {
  return (
    <span className="text-sm text-slate-500">
      {value ? `Last updated ${formatDateTime(value)}` : "Not updated yet"}
    </span>
  );
}

function ErrorMessage({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      {message}
    </div>
  );
}

function EmptyState({ loading, loadingText = "Loading data...", emptyText = "No data available." }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
      {loading ? loadingText : emptyText}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, allLabel = "All" }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none"
      >
        <option value="all">{allLabel}</option>
        {options.map((option) => (
          <option key={String(option)} value={String(option)}>
            {String(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function SearchInput({ label = "Search", value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none"
      />
    </label>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none"
      />
    </label>
  );
}

function TaskStatusBadge({ status }) {
  const category = classifyTaskStatus(status);
  const meta = TASK_STATUS_META[category];
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`} title={status || "Unknown"}>
      {meta.label}
    </span>
  );
}

function Pagination({ page, pageSize, totalRows, onPageChange }) {
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = totalRows === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalRows);

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-slate-500">
        Showing {start}-{end} of {totalRows}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>
        <span className="text-sm text-slate-600">
          Page {safePage} of {pageCount}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(pageCount, safePage + 1))}
          disabled={safePage >= pageCount}
          className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function DistributionList({ title, rows, emptyText = "No data available." }) {
  const max = rows[0]?.value || 1;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h4 className="font-semibold text-slate-900">{title}</h4>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">{emptyText}</p>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.slice(0, 8).map((item) => (
            <div key={String(item.label)}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-slate-700" title={String(item.label)}>{item.label}</span>
                <span className="font-semibold text-slate-900">{item.value}</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-emerald-600"
                  style={{ width: `${Math.max(3, (item.value / max) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimpleLineChart({ target, selectedSeries }) {
  const width = 760;
  const height = 300;
  const padding = { top: 20, right: 22, bottom: 52, left: 64 };

  const visibleSeries = target.series.filter((series) => selectedSeries.includes(series.name));
  const allPoints = visibleSeries.flatMap((series) => series.points);

  if (allPoints.length === 0) {
    return <EmptyState emptyText="No numeric observations are available for the selected series." />;
  }

  const categories = uniqueSorted(allPoints.map((point) => point.x));
  const values = allPoints.map((point) => point.value);
  let minValue = Math.min(...values);
  let maxValue = Math.max(...values);

  if (minValue === maxValue) {
    const offset = Math.abs(minValue || 1) * 0.1;
    minValue -= offset;
    maxValue += offset;
  }

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const xPosition = (x) => {
    const index = Math.max(0, categories.findIndex((category) => String(category) === String(x)));
    if (categories.length <= 1) return padding.left + innerWidth / 2;
    return padding.left + (index / (categories.length - 1)) * innerWidth;
  };
  const yPosition = (value) =>
    padding.top + ((maxValue - value) / (maxValue - minValue)) * innerHeight;

  const yTicks = Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    const value = maxValue - ratio * (maxValue - minValue);
    return { value, y: padding.top + ratio * innerHeight };
  });

  const maxXAxisLabels = 8;
  const xLabelStep = Math.max(1, Math.ceil(categories.length / maxXAxisLabels));

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${target.name} chart`}
        className="min-w-[680px] w-full"
      >
        {yTicks.map((tick) => (
          <g key={tick.y}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={tick.y}
              y2={tick.y}
              stroke="#e2e8f0"
            />
            <text
              x={padding.left - 10}
              y={tick.y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#64748b"
            >
              {formatNumber(tick.value)}
            </text>
          </g>
        ))}

        {categories.map((category, index) => {
          if (index % xLabelStep !== 0 && index !== categories.length - 1) return null;
          return (
            <text
              key={`${category}-${index}`}
              x={xPosition(category)}
              y={height - 18}
              textAnchor="middle"
              fontSize="11"
              fill="#64748b"
            >
              {String(category).slice(0, 14)}
            </text>
          );
        })}

        {visibleSeries.map((series, seriesIndex) => {
          const color = CHART_COLORS[seriesIndex % CHART_COLORS.length];
          const sortedPoints = [...series.points].sort(
            (a, b) => categories.findIndex((item) => String(item) === String(a.x)) - categories.findIndex((item) => String(item) === String(b.x)),
          );
          const path = sortedPoints
            .map((point, pointIndex) => `${pointIndex === 0 ? "M" : "L"} ${xPosition(point.x)} ${yPosition(point.value)}`)
            .join(" ");

          return (
            <g key={series.name}>
              {sortedPoints.length > 1 ? (
                <path d={path} fill="none" stroke={color} strokeWidth="2.5" />
              ) : null}
              {sortedPoints.map((point, pointIndex) => (
                <circle
                  key={`${series.name}-${point.x}-${pointIndex}`}
                  cx={xPosition(point.x)}
                  cy={yPosition(point.value)}
                  r="4"
                  fill={color}
                >
                  <title>
                    {series.name}: {formatNumber(point.value)} {target.unit || ""} ({point.x})
                  </title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function TargetChartCard({ target }) {
  const availableSeries = target.series.map((series) => series.name);
  const [selectedSeries, setSelectedSeries] = useState(availableSeries.slice(0, 6));

  useEffect(() => {
    setSelectedSeries(availableSeries.slice(0, 6));
  }, [target.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSeries = (seriesName) => {
    setSelectedSeries((current) =>
      current.includes(seriesName)
        ? current.filter((item) => item !== seriesName)
        : [...current, seriesName],
    );
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h4 className="text-xl font-semibold text-slate-900">{target.name}</h4>
          <p className="mt-1 text-sm text-slate-500">
            {target.unit ? `Unit: ${target.unit}` : "Unit not provided"}
          </p>
          {target.description ? <p className="mt-2 text-sm text-slate-600">{target.description}</p> : null}
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
          {target.series.length} {target.series.length === 1 ? "series" : "series"}
        </span>
      </div>

      {availableSeries.length > 1 ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visible series</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {availableSeries.map((seriesName) => {
              const selected = selectedSeries.includes(seriesName);
              return (
                <button
                  key={seriesName}
                  type="button"
                  onClick={() => toggleSeries(seriesName)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    selected
                      ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {seriesName}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <SimpleLineChart target={target} selectedSeries={selectedSeries} />
      </div>
    </article>
  );
}

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Target chart rendering error", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
          This target could not be displayed, but the remaining charts are still available.
        </div>
      );
    }

    return this.props.children;
  }
}

export default function ScenathonProcessMonitor() {
  const { authFetch } = useAuth();
  const mountedRef = useRef(true);
  const taskRequestRef = useRef(0);
  const tradeRequestRef = useRef(0);
  const targetsRequestRef = useRef(0);
  const detailsRequestRef = useRef(0);
  // Stores the scenathon currently being requested per section.
  // This prevents duplicate requests for the same scenathon without blocking
  // a new request when the user changes the selected scenathon.
  const inFlightRef = useRef({ tasks: null, trade: null, targets: null });

  const [activeTab, setActiveTab] = useState("tasks");
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(60);
  const [scenathons, setScenathons] = useState([]);
  const [scenathonsLoading, setScenathonsLoading] = useState(true);
  const [scenathonsError, setScenathonsError] = useState(null);
  const [scenathonSearch, setScenathonSearch] = useState("");
  const [selectedScenathonId, setSelectedScenathonId] = useState(null);
  const [details, setDetails] = useState(INITIAL_DETAILS);
  const [tasksState, setTasksState] = useState(INITIAL_DATA_STATE);
  const [taskColnames, setTaskColnames] = useState([]);
  const [tradeState, setTradeState] = useState(INITIAL_DATA_STATE);
  const [targetsState, setTargetsState] = useState({ ...INITIAL_DATA_STATE, targets: [] });
  const [taskFilters, setTaskFilters] = useState(INITIAL_TASK_FILTERS);
  const [tradeFilters, setTradeFilters] = useState(INITIAL_TRADE_FILTERS);
  const [targetSearch, setTargetSearch] = useState("");
  const [taskPage, setTaskPage] = useState(1);
  const [tradePage, setTradePage] = useState(1);

  const pageSize = 12;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadScenathons() {
      setScenathonsLoading(true);
      setScenathonsError(null);

      try {
        const data = await fetchJson(
          authFetch,
          ENDPOINTS.scenathons,
          "Error loading scenathons",
        );
        if (!alive) return;

        const rows = asArray(data, ["scenathons", "rows"]);
        setScenathons(rows);
        setSelectedScenathonId((current) => current || rows[0]?.id || null);
      } catch (error) {
        if (!alive) return;
        console.error(error);
        setScenathonsError(error?.message || "Error loading scenathons");
      } finally {
        if (alive) setScenathonsLoading(false);
      }
    }

    loadScenathons();
    return () => {
      alive = false;
    };
  }, [authFetch]);

  const refreshDetails = useCallback(async () => {
    if (!selectedScenathonId) {
      setDetails(INITIAL_DETAILS);
      return;
    }

    const requestId = ++detailsRequestRef.current;
    setDetails((current) => ({ ...current, loading: true, error: null }));

    try {
      const [availableData, defaultData] = await Promise.all([
        fetchJson(
          authFetch,
          ENDPOINTS.availablePathways(selectedScenathonId),
          "Error loading available pathways",
        ),
        fetchJson(
          authFetch,
          ENDPOINTS.defaultPathways(selectedScenathonId),
          "Error loading default pathways",
        ),
      ]);

      if (!mountedRef.current || requestId !== detailsRequestRef.current) return;

      setDetails({
        availablePathways: asArray(availableData, ["pathways", "rows"]),
        defaultPathways: asArray(defaultData, ["defaultPathways", "rows"]),
        loading: false,
        error: null,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== detailsRequestRef.current) return;
      console.error(error);
      setDetails({
        availablePathways: [],
        defaultPathways: [],
        loading: false,
        error: error?.message || "Error loading process details",
      });
    }
  }, [authFetch, selectedScenathonId]);

  const loadTaskColnames = useCallback(async () => {
    try {
      const data = await fetchJson(
        authFetch,
        ENDPOINTS.taskColnames,
        "Error loading task names",
      );
      setTaskColnames(asArray(data, ["colnames"]));
    } catch (error) {
      console.error("Error loading task colnames:", error);
      setTaskColnames([]);
    }
  }, [authFetch]);

  const refreshTasks = useCallback(async () => {
    if (!selectedScenathonId || inFlightRef.current.tasks === selectedScenathonId) return;

    const scenathonId = selectedScenathonId;
    inFlightRef.current.tasks = scenathonId;
    const requestId = ++taskRequestRef.current;
    setTasksState((current) => ({ ...current, loading: true, error: null }));

    try {
      const data = await fetchJson(
        authFetch,
        ENDPOINTS.tasks(
          taskPage,
          pageSize,
          taskFilters.search,
          taskFilters.name !== "all" ? taskFilters.name : undefined,
        ),
        "Error loading task activity",
      );
      if (!mountedRef.current || requestId !== taskRequestRef.current) return;

      const rows = asArray(data, ["rows", "tasks"])
        .filter((row) => Number(row.scenathon_id) === Number(scenathonId));

      setTasksState({
        rows,
        loading: false,
        error: null,
        lastRefreshed: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== taskRequestRef.current) return;
      console.error(error);
      setTasksState((current) => ({
        ...current,
        loading: false,
        error: error?.message || "Error loading task activity",
      }));
    } finally {
      if (inFlightRef.current.tasks === scenathonId) {
        inFlightRef.current.tasks = null;
      }
    }
  }, [authFetch, pageSize, selectedScenathonId, taskFilters.search, taskPage]);

  const refreshTrade = useCallback(async () => {
    if (!selectedScenathonId || inFlightRef.current.trade === selectedScenathonId) return;

    const scenathonId = selectedScenathonId;
    inFlightRef.current.trade = scenathonId;
    const requestId = ++tradeRequestRef.current;
    setTradeState((current) => ({ ...current, loading: true, error: null }));

    try {
      const data = await fetchJson(
        authFetch,
        ENDPOINTS.tradeSubmissions(scenathonId),
        "Error loading trade adjustment submissions",
      );
      if (!mountedRef.current || requestId !== tradeRequestRef.current) return;

      setTradeState({
        rows: asArray(data, ["rows", "submissions"]),
        loading: false,
        error: null,
        lastRefreshed: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== tradeRequestRef.current) return;
      console.error(error);
      setTradeState((current) => ({
        ...current,
        loading: false,
        error: error?.message || "Error loading trade adjustment submissions",
      }));
    } finally {
      if (inFlightRef.current.trade === scenathonId) {
        inFlightRef.current.trade = null;
      }
    }
  }, [authFetch, selectedScenathonId]);

  const refreshTargets = useCallback(async () => {
    if (!selectedScenathonId || inFlightRef.current.targets === selectedScenathonId) return;

    const scenathonId = selectedScenathonId;
    inFlightRef.current.targets = scenathonId;
    const requestId = ++targetsRequestRef.current;
    setTargetsState((current) => ({ ...current, loading: true, error: null }));

    try {
      const data = await fetchJson(
        authFetch,
        ENDPOINTS.targetsSummary(scenathonId),
        "Error loading Targets Summary",
      );
      if (!mountedRef.current || requestId !== targetsRequestRef.current) return;

      const targets = normalizeTargetsPayload(data);
      setTargetsState({
        rows: asArray(data, ["rows", "data", "values"]),
        targets,
        loading: false,
        error: null,
        lastRefreshed: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== targetsRequestRef.current) return;
      console.error(error);
      setTargetsState((current) => ({
        ...current,
        loading: false,
        error: error?.message || "Error loading Targets Summary",
      }));
    } finally {
      if (inFlightRef.current.targets === scenathonId) {
        inFlightRef.current.targets = null;
      }
    }
  }, [authFetch, selectedScenathonId]);

  const refreshActiveSection = useCallback(() => {
    if (activeTab === "tasks") return refreshTasks();
    if (activeTab === "trade") return refreshTrade();
    return refreshTargets();
  }, [activeTab, refreshTargets, refreshTasks, refreshTrade]);

  useEffect(() => {
    if (!selectedScenathonId) return;

    setTaskPage(1);
    setTradePage(1);
    loadTaskColnames();
    refreshDetails();
    refreshTasks();
    refreshTrade();
    refreshTargets();
  }, [selectedScenathonId, refreshDetails, refreshTargets, refreshTrade, loadTaskColnames]);

  useEffect(() => {
    if (!selectedScenathonId) return;
    refreshTasks();
  }, [selectedScenathonId, refreshTasks]);

  useEffect(() => {
    if (!selectedScenathonId || autoRefreshSeconds <= 0) return undefined;

    const intervalId = window.setInterval(() => {
      refreshActiveSection();
    }, autoRefreshSeconds * 1000);

    return () => window.clearInterval(intervalId);
  }, [autoRefreshSeconds, refreshActiveSection, selectedScenathonId]);

  const filteredScenathons = useMemo(() => {
    const term = normalizeText(scenathonSearch);
    if (!term) return scenathons;

    return scenathons.filter((item) =>
      [item.name, item.status, item.register, item.type, item.year]
        .filter(Boolean)
        .some((value) => normalizeText(value).includes(term)),
    );
  }, [scenathonSearch, scenathons]);

  const selectedScenathon = useMemo(
    () => scenathons.find((item) => Number(item.id) === Number(selectedScenathonId)) || null,
    [scenathons, selectedScenathonId],
  );

  const scenathonSummary = useMemo(() => ({
    total: scenathons.length,
    active: scenathons.filter((item) => normalizeText(item.status) === "active").length,
    inactive: scenathons.filter((item) => normalizeText(item.status) === "inactive").length,
  }), [scenathons]);

  const taskOptions = useMemo(() => ({
    statuses: uniqueSorted(tasksState.rows.map((row) => classifyTaskStatus(row.status))),
    countries: uniqueSorted(tasksState.rows.map((row) => row.country)),
    users: uniqueSorted(tasksState.rows.map((row) => displayUser(row))),
    names: uniqueSorted(taskColnames),
  }), [tasksState.rows, taskColnames]);

  const filteredTasks = useMemo(() => {
    const searchTerm = normalizeText(taskFilters.search);

    return [...tasksState.rows]
      .filter((row) => {
        if (taskFilters.status !== "all" && classifyTaskStatus(row.status) !== taskFilters.status) return false;
        if (taskFilters.country !== "all" && String(row.country) !== taskFilters.country) return false;
        if (taskFilters.user !== "all" && displayUser(row) !== taskFilters.user) return false;
        if (taskFilters.name !== "all" && String(row.name) !== taskFilters.name) return false;
        if (!inDateRange(row.created_at, taskFilters.dateFrom, taskFilters.dateTo)) return false;

        if (!searchTerm) return true;
        return [row.id, row.task_id, row.country, row.name, row.colname, row.colvalue, row.status, displayUser(row)]
          .filter((value) => value !== null && value !== undefined)
          .some((value) => normalizeText(value).includes(searchTerm));
      })
      .sort((a, b) => (toTimestamp(b.created_at) || 0) - (toTimestamp(a.created_at) || 0));
  }, [taskFilters, tasksState.rows]);

  useEffect(() => {
    setTaskPage(1);
  }, [taskFilters]);

  const taskSummary = useMemo(() => {
    const statusCounts = {
      pending: 0,
      in_progress: 0,
      completed: 0,
      error: 0,
      unknown: 0,
    };

    tasksState.rows.forEach((row) => {
      statusCounts[classifyTaskStatus(row.status)] += 1;
    });

    return {
      total: tasksState.rows.length,
      ...statusCounts,
      byCountry: countBy(tasksState.rows, (row) => row.country || "Unknown"),
      byName: countBy(tasksState.rows, (row) => row.name || "Unknown"),
    };
  }, [tasksState.rows]);

  const paginatedTasks = filteredTasks.slice((taskPage - 1) * pageSize, taskPage * pageSize);

  const tradeOptions = useMemo(() => ({
    pathways: uniqueSorted(tradeState.rows.map((row) => row.pathway_name)),
    countries: uniqueSorted(tradeState.rows.map((row) => row.country)),
    users: uniqueSorted(tradeState.rows.map((row) => displayUser(row))),
  }), [tradeState.rows]);

  const filteredTrade = useMemo(() => {
    const searchTerm = normalizeText(tradeFilters.search);

    return [...tradeState.rows]
      .filter((row) => {
        if (tradeFilters.pathway !== "all" && String(row.pathway_name) !== tradeFilters.pathway) return false;
        if (tradeFilters.country !== "all" && String(row.country) !== tradeFilters.country) return false;
        if (tradeFilters.user !== "all" && displayUser(row) !== tradeFilters.user) return false;
        if (!inDateRange(row.sent_at || row.created_at, tradeFilters.dateFrom, tradeFilters.dateTo)) return false;

        if (!searchTerm) return true;
        return [row.id, row.pathway_id, row.pathway_name, row.country, displayUser(row)]
          .filter((value) => value !== null && value !== undefined)
          .some((value) => normalizeText(value).includes(searchTerm));
      })
      .sort((a, b) => (toTimestamp(b.sent_at) || 0) - (toTimestamp(a.sent_at) || 0));
  }, [tradeFilters, tradeState.rows]);

  useEffect(() => {
    setTradePage(1);
  }, [tradeFilters]);

  const tradeSummary = useMemo(() => ({
    total: tradeState.rows.length,
    countries: uniqueSorted(tradeState.rows.map((row) => row.country)).length,
    pathways: uniqueSorted(tradeState.rows.map((row) => row.pathway_name)).length,
    users: uniqueSorted(tradeState.rows.map((row) => displayUser(row))).length,
    latest: getLatestDate(tradeState.rows, (row) => row.sent_at || row.updated_at),
    byCountry: countBy(tradeState.rows, (row) => row.country || "Unknown"),
    byPathway: countBy(tradeState.rows, (row) => row.pathway_name || "Unknown"),
  }), [tradeState.rows]);

  const paginatedTrade = filteredTrade.slice((tradePage - 1) * pageSize, tradePage * pageSize);

  const filteredTargets = useMemo(() => {
    const term = normalizeText(targetSearch);
    if (!term) return targetsState.targets;
    return targetsState.targets.filter((target) =>
      [target.name, target.unit, target.description]
        .filter(Boolean)
        .some((value) => normalizeText(value).includes(term)),
    );
  }, [targetSearch, targetsState.targets]);

  const currentLoading =
    activeTab === "tasks"
      ? tasksState.loading
      : activeTab === "trade"
        ? tradeState.loading
        : targetsState.loading;

  const currentLastRefreshed =
    activeTab === "tasks"
      ? tasksState.lastRefreshed
      : activeTab === "trade"
        ? tradeState.lastRefreshed
        : targetsState.lastRefreshed;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <header className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Scenathon Process Monitor</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Monitor task execution, trade-adjustment submissions and every target available in Targets Summary from one user-friendly dashboard.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard label="Scenathons" value={scenathonSummary.total} />
            <MetricCard label="Active" value={scenathonSummary.active} tone="emerald" />
            <MetricCard label="Inactive" value={scenathonSummary.inactive} tone="rose" />
          </div>
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-[1fr_1.5fr_0.8fr]">
          <SearchInput
            label="Find scenathon"
            value={scenathonSearch}
            onChange={setScenathonSearch}
            placeholder="Name, year, type or status"
          />

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected scenathon</span>
            <select
              value={selectedScenathonId || ""}
              onChange={(event) => setSelectedScenathonId(Number(event.target.value) || null)}
              disabled={scenathonsLoading}
              className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none disabled:opacity-60"
            >
              <option value="">Select a scenathon</option>
              {filteredScenathons.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} • {item.year || "N/A"} • {STATUS_LABELS[item.status] || item.status || "Unknown"}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Automatic refresh</span>
            <select
              value={autoRefreshSeconds}
              onChange={(event) => setAutoRefreshSeconds(Number(event.target.value))}
              className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none"
            >
              {AUTO_REFRESH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        {selectedScenathon ? (
          <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-slate-900">{selectedScenathon.name}</span>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLASSES[selectedScenathon.status] || "bg-slate-100 text-slate-700"}`}>
              {STATUS_LABELS[selectedScenathon.status] || selectedScenathon.status || "Unknown"}
            </span>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TYPE_CLASSES[selectedScenathon.type] || "bg-slate-100 text-slate-700"}`}>
              {TYPE_LABELS[selectedScenathon.type] || selectedScenathon.type || "Unknown type"}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {details.availablePathways.length} recognized pathways
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {details.defaultPathways.length} priority pathways
            </span>
          </div>
        ) : null}

        <ErrorMessage message={scenathonsError || details.error} />
      </header>

      <nav className="mt-6 rounded-3xl border border-slate-200 bg-white p-2 shadow-sm" aria-label="Monitoring sections">
        <div className="grid gap-2 md:grid-cols-3">
          {TAB_ITEMS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? "bg-emerald-700 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="mt-6 space-y-6">
        {!selectedScenathonId ? (
          <EmptyState emptyText="Select a scenathon to start monitoring its activity." />
        ) : null}

        {selectedScenathonId && activeTab === "tasks" ? (
          <section className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                eyebrow="tasktodo"
                title="Task Monitoring"
                description="Review task volume, execution status, recent activity and the distribution of work by country and task type."
                actions={(
                  <>
                    <RefreshButton loading={tasksState.loading} onClick={refreshTasks} />
                    <LastUpdated value={tasksState.lastRefreshed} />
                  </>
                )}
              />

              <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <MetricCard label="Total tasks" value={taskSummary.total} />
                <MetricCard label="Pending" value={taskSummary.pending} tone="amber" />
                <MetricCard label="In progress" value={taskSummary.in_progress} tone="sky" />
                <MetricCard label="Completed" value={taskSummary.completed} tone="emerald" />
                <MetricCard label="Errors / unknown" value={taskSummary.error + taskSummary.unknown} tone="rose" />
              </div>
            </div>

            <ErrorMessage message={tasksState.error} />

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
                <SearchInput
                  value={taskFilters.search}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, search: value }))}
                  placeholder="Task, country, value..."
                />
                <FilterSelect
                  label="Status"
                  value={taskFilters.status}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, status: value }))}
                  options={taskOptions.statuses}
                  allLabel="All statuses"
                />
                <FilterSelect
                  label="Country"
                  value={taskFilters.country}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, country: value }))}
                  options={taskOptions.countries}
                  allLabel="All countries"
                />
                <FilterSelect
                  label="User"
                  value={taskFilters.user}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, user: value }))}
                  options={taskOptions.users}
                  allLabel="All users"
                />
                <FilterSelect
                  label="Task name"
                  value={taskFilters.name}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, name: value }))}
                  options={taskOptions.names}
                  allLabel="All task names"
                />
                <DateInput
                  label="From"
                  value={taskFilters.dateFrom}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, dateFrom: value }))}
                />
                <DateInput
                  label="To"
                  value={taskFilters.dateTo}
                  onChange={(value) => setTaskFilters((current) => ({ ...current, dateTo: value }))}
                />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setTaskFilters(INITIAL_TASK_FILTERS)}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  Clear filters
                </button>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <DistributionList title="Tasks by country" rows={taskSummary.byCountry} />
              <DistributionList title="Tasks by type" rows={taskSummary.byName} />
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-100 text-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Created at</th>
                      <th className="px-4 py-3 text-left font-semibold">Task</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Country</th>
                      <th className="px-4 py-3 text-left font-semibold">Name</th>
                      <th className="px-4 py-3 text-left font-semibold">User</th>
                      <th className="px-4 py-3 text-left font-semibold">Column / value</th>
                      <th className="px-4 py-3 text-left font-semibold">On PC</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {paginatedTasks.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTime(row.created_at)}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{row.task_id ?? row.id ?? "-"}</td>
                        <td className="px-4 py-3"><TaskStatusBadge status={row.status} /></td>
                        <td className="px-4 py-3">{row.country || "-"}</td>
                        <td className="px-4 py-3">{row.name || "-"}</td>
                        <td className="max-w-[220px] truncate px-4 py-3" title={displayUser(row)}>{displayUser(row)}</td>
                        <td className="max-w-[260px] px-4 py-3 text-slate-600">
                          <span className="font-medium text-slate-800">{row.colname || "-"}</span>
                          {row.colvalue !== null && row.colvalue !== undefined ? `: ${row.colvalue}` : ""}
                        </td>
                        <td className="px-4 py-3">{row.onpc ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredTasks.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    loading={tasksState.loading}
                    loadingText="Loading task activity..."
                    emptyText="No tasks match the selected filters."
                  />
                </div>
              ) : (
                <Pagination
                  page={taskPage}
                  pageSize={pageSize}
                  totalRows={filteredTasks.length}
                  onPageChange={setTaskPage}
                />
              )}
            </div>
          </section>
        ) : null}

        {selectedScenathonId && activeTab === "trade" ? (
          <section className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                eyebrow="trade_adjustment_submissions"
                title="Trade Adjustment Monitoring"
                description="Track which countries and pathways have submitted trade adjustments, who submitted them and when the latest activity occurred."
                actions={(
                  <>
                    <RefreshButton loading={tradeState.loading} onClick={refreshTrade} />
                    <LastUpdated value={tradeState.lastRefreshed} />
                  </>
                )}
              />

              <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <MetricCard label="Total submissions" value={tradeSummary.total} />
                <MetricCard label="Countries" value={tradeSummary.countries} tone="emerald" />
                <MetricCard label="Pathways" value={tradeSummary.pathways} tone="violet" />
                <MetricCard label="Users" value={tradeSummary.users} tone="sky" />
                <MetricCard label="Latest submission" value={tradeSummary.latest ? formatDate(tradeSummary.latest) : "-"} tone="amber" />
              </div>
            </div>

            <ErrorMessage message={tradeState.error} />

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <SearchInput
                  value={tradeFilters.search}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, search: value }))}
                  placeholder="Pathway, country, user..."
                />
                <FilterSelect
                  label="Pathway"
                  value={tradeFilters.pathway}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, pathway: value }))}
                  options={tradeOptions.pathways}
                  allLabel="All pathways"
                />
                <FilterSelect
                  label="Country"
                  value={tradeFilters.country}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, country: value }))}
                  options={tradeOptions.countries}
                  allLabel="All countries"
                />
                <FilterSelect
                  label="User"
                  value={tradeFilters.user}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, user: value }))}
                  options={tradeOptions.users}
                  allLabel="All users"
                />
                <DateInput
                  label="From"
                  value={tradeFilters.dateFrom}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, dateFrom: value }))}
                />
                <DateInput
                  label="To"
                  value={tradeFilters.dateTo}
                  onChange={(value) => setTradeFilters((current) => ({ ...current, dateTo: value }))}
                />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setTradeFilters(INITIAL_TRADE_FILTERS)}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  Clear filters
                </button>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <DistributionList title="Submissions by country" rows={tradeSummary.byCountry} />
              <DistributionList title="Submissions by pathway" rows={tradeSummary.byPathway} />
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-100 text-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Submitted at</th>
                      <th className="px-4 py-3 text-left font-semibold">Pathway</th>
                      <th className="px-4 py-3 text-left font-semibold">Country</th>
                      <th className="px-4 py-3 text-left font-semibold">Submitted by</th>
                      <th className="px-4 py-3 text-left font-semibold">Created</th>
                      <th className="px-4 py-3 text-left font-semibold">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {paginatedTrade.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTime(row.sent_at)}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{row.pathway_name || "-"}</td>
                        <td className="px-4 py-3">{row.country || "-"}</td>
                        <td className="max-w-[240px] truncate px-4 py-3" title={displayUser(row)}>{displayUser(row)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTime(row.created_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTime(row.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredTrade.length === 0 ? (
                <div className="p-4">
                  <EmptyState
                    loading={tradeState.loading}
                    loadingText="Loading trade adjustment submissions..."
                    emptyText="No submissions match the selected filters."
                  />
                </div>
              ) : (
                <Pagination
                  page={tradePage}
                  pageSize={pageSize}
                  totalRows={filteredTrade.length}
                  onPageChange={setTradePage}
                />
              )}
            </div>
          </section>
        ) : null}

        {selectedScenathonId && activeTab === "targets" ? (
          <section className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeader
                eyebrow="Targets Summary"
                title="Target Monitoring"
                description="A separate chart is generated automatically for every target returned by the Targets Summary data source."
                actions={(
                  <>
                    <RefreshButton loading={targetsState.loading} onClick={refreshTargets} />
                    <LastUpdated value={targetsState.lastRefreshed} />
                  </>
                )}
              />

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <MetricCard label="Available targets" value={targetsState.targets.length} />
                <MetricCard
                  label="Total series"
                  value={targetsState.targets.reduce((total, target) => total + target.series.length, 0)}
                  tone="violet"
                />
                <MetricCard
                  label="Visible charts"
                  value={filteredTargets.length}
                  tone="emerald"
                />
              </div>
            </div>

            <ErrorMessage message={targetsState.error} />

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <SearchInput
                label="Find target"
                value={targetSearch}
                onChange={setTargetSearch}
                placeholder="Target name, description or unit"
              />
            </div>

            {filteredTargets.length === 0 ? (
              <EmptyState
                loading={targetsState.loading}
                loadingText="Loading Targets Summary..."
                emptyText="No targets are available for this scenathon or no targets match the search."
              />
            ) : (
              <div className="space-y-6">
                {filteredTargets.map((target) => (
                  <ChartErrorBoundary key={target.id}>
                    <TargetChartCard target={target} />
                  </ChartErrorBoundary>
                ))}
              </div>
            )}
          </section>
        ) : null}
      </main>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500 shadow-sm">
        <span>
          {currentLoading ? "Loading data..." : "Dashboard ready"}
        </span>
        <span>
          {currentLastRefreshed ? `Current section updated ${formatDateTime(currentLastRefreshed)}` : "Current section has not been refreshed"}
        </span>
      </div>
    </div>
  );
}